<script lang="ts" setup>
import type { RoutesNamesList } from '@typed-router/__routes'
import PathAutocomplete from '@d/form/components/PathAutocomplete.vue'
import UIcon from '@d/form/components/UIcon.vue'
import { mdiCheck, mdiChevronRight, mdiClose, mdiDotsHorizontal, mdiHome, mdiMagnify, mdiMagnifyClose, mdiPencil, mdiPlus } from '@mdi/js'
import { breadcrumbState } from '../../composables/useBreadcrumbs'
import { icon } from '../../helper/icon'

import { useStateScope } from '../../helper/shortcut'
import { getTenant } from '../../helper/tenant'
import { handleFileSelect } from '../tree/helper/fileSelect'
import { flatTreeItems, opened, selected } from '../tree/treeMapper'
import Fitter from '../util/Fitter.vue'
import Shortcut from '../util/Shortcut.vue'

const route = useRoute('client-view-path')
const router = useRouter()

const otherView = computed(() => route.meta.layout !== 'default' && route.meta.layout !== undefined) // only show toolbar in default layout

const state = breadcrumbState.state

const hasPath = computed(() => route.params.path && route.params.path.length > 0)

const editMode = ref(false)

const { t } = useI18n({ useScope: 'local' })

const tenant = getTenant()
const searchQuery = useSessionStorage(`${tenant}:search:query`, '')
const showSearch = useSessionStorage(`${tenant}:search:show`, false)
const searchHistory = useLocalStorage<string[]>(`${tenant}:search:history`, [])
const refresh = ref(0)

watch(showSearch, () => {
  refresh.value++
})

const debouncedSearchQuery = debouncedRef(searchQuery, 500)

const searchHistoryFiltered = computed(() => {
  if (!searchQuery.value)
    return []

  return searchHistory.value.filter(h => h.toLowerCase().includes(searchQuery.value.toLowerCase()) && h.toLowerCase() !== searchQuery.value.toLowerCase())
})

const breadcrumbs = computed(() => {
  // if add mode, show full path (which would otherwise be handled by currentBreadcrumb) for easier styling
  if (!route.params.path)
    return []

  const p = state.value.mode === 'add' ? route.params.path : route.params.path.slice(0, -1)

  return p.map((e, i) => {
    const item = flatTreeItems.get(route.params.path.slice(0, i + 1).join('/'))

    return {
      name: e,
      path: route.params.path.slice(0, i + 1),
      icon: item ? icon(item) : undefined,
    }
  })
})

const currentBreadcrumb = computed(() => {
  const item = flatTreeItems.get(route.params.path.join('/'))
  return {
    name: route.params.path[route.params.path.length - 1],
    path: route.params.path,
    icon: item ? icon(item) : undefined,
  }
})

const currentRoute = computed(() => {
  return router.currentRoute.value
})

function navigatePath(b: { name: string, path: string[] }): void {
  navigateTo({ name: 'client-view-path', params: { client: route.params.client, path: b.path } })
}

function navigateBack(): void {
  showSearch.value = false
  navigateTo({ name: route.query.redirect as RoutesNamesList, params: { client: route.params.client, path: route.params.path } })
}

function navigateHome(): void {
  opened.value = []
  selected.value = []
  showSearch.value = false
  navigateTo({ name: 'client', params: { client: route.params.client } })
}

function clickSearch(value: boolean, clearQuery: boolean = false): void {
  if (!value) {
    showSearch.value = false
  }
  if (clearQuery) {
    searchQuery.value = undefined
  }
  if (value || currentRoute.value.name !== 'client-search-path') { // Only hide search field when not in search component
    showSearch.value = value
    // Highlight everything
    nextTick(selectAllSearchbar)
  }
  const searchEmpty = searchQuery.value === undefined || searchQuery.value === ''
  if (value && !searchEmpty) {
    navigateSearch()
  }
}

watch(() => currentRoute.value.name, (value, oldValue) => {
  if (oldValue === 'client-search-path' && value !== 'client-search-path') {
    showSearch.value = false
  }
})

function navigateSearch(): void {
  if (currentRoute.value.name !== 'client-search-path') {
    navigateTo({ name: 'client-search-path', params: { client: route.params.client, path: route.params.path }, query: { redirect: route.name } })
  }
}

watch(debouncedSearchQuery, (query) => {
  const searchEmpty = query === undefined || query === ''
  if (!searchEmpty) {
    navigateSearch()
  }
})

const searchEl = templateRef('searchEl')
const shortcutScope = useStateScope()

// When built, some component refs don't expose a `focused` property reliably.
// Use the actual DOM activeElement to detect whether the search input is focused
// so we don't accidentally prevent the browser's Ctrl+F behavior in production.
function isSearchFocused(): boolean {
  const el: any = searchEl.value?.$el ?? searchEl.value
  if (!el || typeof document === 'undefined')
    return false
  try {
    return typeof el.contains === 'function' && el.contains(document.activeElement)
  }
  catch {
    return false
  }
}

// Track quick double-presses of Ctrl/Cmd+F so a rapid second press opens the
// browser find instead of focusing the app search. We store the timestamp of
// the last find key press and a flag to signal the whenever handlers to skip
// focusing the internal search when a double-press is detected.
const lastFindPressAt = ref(0)
const skipInternalFind = ref(false)
// Timer for a pending internal-search activation. We schedule the focus for
// a short time instead of doing it immediately so a quick second press of
// Ctrl/Cmd+F can cancel it and allow the browser find to open without a
// brief internal focus flash.
const pendingFindTimer = ref<number | null>(null)
// ms -- tuned for quick double-press
const DOUBLE_PRESS_THRESHOLD = 400

const { ctrl_f, cmd_f } = useMagicKeys({
  passive: false,
  onEventFired(e) {
    if (shortcutScope?.dialog.value || shortcutScope?.isLoadingBlocking.value)
      return

    if (isSearchFocused()) {
      // Only swallow Escape when the search field is focused.
      if (e.key === 'Escape' && e.type === 'keydown') {
        e.preventDefault()
      }
      return
    }

    // Detect Ctrl/Cmd+F keydown sequences. If the user presses Ctrl/Cmd+F twice
    // quickly (within the threshold), treat the second press as an intent to
    // open the browser find: do NOT preventDefault and mark skipInternalFind
    // so the `whenever` handlers won't focus the app search.
    const isFindCombination = (e.ctrlKey || e.metaKey) && e.key && e.key.toLowerCase() === 'f' && e.type === 'keydown'
    if (isFindCombination) {
      const now = Date.now()
      const dt = now - lastFindPressAt.value
      lastFindPressAt.value = now

      if (dt <= DOUBLE_PRESS_THRESHOLD) {
        // quick double-press: allow browser default and skip internal focusing
        skipInternalFind.value = true
        // clear the flag shortly after to avoid affecting unrelated presses
        setTimeout(() => {
          skipInternalFind.value = false
        }, DOUBLE_PRESS_THRESHOLD + 50)
        // cancel any pending internal focus scheduled previously
        if (pendingFindTimer.value !== null) {
          clearTimeout(pendingFindTimer.value)
          pendingFindTimer.value = null
        }
        // don't call e.preventDefault() so browser find can appear
      }
      else {
        // single press: prevent browser find and schedule internal handling
        if (new Date().getTime() < lastFindPressAt.value + DOUBLE_PRESS_THRESHOLD)
          e.preventDefault()
      }
    }
  },
})

whenever(ctrl_f, () => {
  if (shortcutScope?.dialog.value || shortcutScope?.isLoadingBlocking.value)
    return

  // If a quick double-press was detected we skip focusing the internal
  // search so the browser's find can open instead.
  if (skipInternalFind.value) {
    skipInternalFind.value = false
    return
  }

  if (isSearchFocused())
    return

  // Schedule the internal search activation after the double-press window
  // so a quick second Ctrl+F can cancel it and the browser find appears.
  if (pendingFindTimer.value !== null) {
    clearTimeout(pendingFindTimer.value)
    pendingFindTimer.value = null
  }
  pendingFindTimer.value = window.setTimeout(() => {
    pendingFindTimer.value = null
    clickSearch(true)
    selectAllSearchbar()
  }, DOUBLE_PRESS_THRESHOLD)
})

whenever(cmd_f, () => {
  if (isSearchFocused())
    return

  // Respect a quick double-press for Cmd+F as well.
  if (skipInternalFind.value) {
    skipInternalFind.value = false
    return
  }

  if (pendingFindTimer.value !== null) {
    clearTimeout(pendingFindTimer.value)
    pendingFindTimer.value = null
  }
  pendingFindTimer.value = window.setTimeout(() => {
    pendingFindTimer.value = null
    clickSearch(true)
    if (searchEl.value) {
      const el: any = searchEl.value.$el ?? searchEl.value
      const input = el?.querySelector?.('input')
      if (input) {
        input.focus()
        input.selectionStart = 0
        input.selectionEnd = searchQuery.value.length
      }
      else if (typeof searchEl.value.focus === 'function') {
        searchEl.value.focus()
        try {
          searchEl.value.selectionStart = 0
          searchEl.value.selectionEnd = searchQuery.value.length
        }
        catch {
          /* ignore */
        }
      }
    }
  }, DOUBLE_PRESS_THRESHOLD)
})

const newPath = ref<null | string[]>(null)
const currentPath = computed({ get: () => route.params.path, set: (value: string[]) => newPath.value = value })
function selectAllSearchbar() {
  if (searchEl.value) {
    // focus the inner input if possible, otherwise focus the component
    const el: any = searchEl.value.$el ?? searchEl.value
    const input = el?.querySelector?.('input')
    if (input) {
      input.focus()
      input.selectionStart = 0
      input.selectionEnd = searchQuery.value.length
    }
    else if (typeof searchEl.value.focus === 'function') {
      searchEl.value.focus()
      try {
        searchEl.value.selectionStart = 0
        searchEl.value.selectionEnd = searchQuery.value.length
      }
      catch {
        /* ignore */
      }
    }
  }
}

function handleEdit() {
  if (!editMode.value) {
    editMode.value = true
  }
  else if (newPath.value) {
    handleFileSelect(route.params.client, newPath.value)
    newPath.value = null
    editMode.value = false
  }
  else {
    editMode.value = false
  }
}
</script>

<template>
  <v-toolbar v-if="!otherView" class="toolbar" color="transparent" scroll-behavior="elevate">
    <div class="file-toolbar">
      <slot name="prepend" />
      <div class="file-info">
        <Fitter v-slot="{ breaking }">
          <div class="file-path" :class="{ 'is-breaking': breaking }">
            <div style="display: flex; width: 100%;">
              <v-chip v-if="!editMode" rounded="0" variant="text" :disabled="currentRoute.name === 'client' || (shortcutScope?.isLoadingBlocking.value ?? false)" @click="navigateHome()">
                <UIcon :icon="mdiHome" />
              </v-chip>
              <div style="display: flex; justify-content: space-between; width: 100%;">
                <div style="display: flex; flex-grow: 1">
                  <div class="compressed">
                    <v-menu v-if="!showSearch">
                      <template #activator="{ props: props2 }">
                        <v-chip rounded="0" variant="text" v-bind="props2">
                          <UIcon :icon="mdiDotsHorizontal" />
                        </v-chip>
                      </template>
                      <v-list>
                        <v-list-item v-for="(b, index) in breadcrumbs" :key="index" :value="index">
                          <v-list-item-title
                            :style="{ 'margin-left': `${(Math.max(index - 1, 0)) * 8}px` }"
                            @click="navigatePath(b)"
                          >
                            <UIcon v-if="index > 0" :icon="mdiChevronRight" style="opacity: 0.6" size="16" class="mr-2" />
                            <UIcon v-if="b.icon" :icon="b.icon" class="mr-2" size="22" /> {{ b.name }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>

                    <UIcon :icon="mdiChevronRight" style="opacity: 0.8" size="16" />
                  </div>

                  <!-- Breadcrumbs -->
                  <div class="d-flex" :class="{ 'search-field': showSearch }">
                    <div v-if="!showSearch && !editMode">
                      <v-chip rounded="0" variant="text" @click="() => clickSearch(true, false)">
                        <UIcon :icon="mdiMagnify" />
                        <Shortcut keys="ctrl+f" only-display :disabled="shortcutScope?.dialog.value || shortcutScope?.isLoadingBlocking.value" color="white" />
                      </v-chip>
                    </div>
                    <div v-if="showSearch" style="display: flex; flex-grow:1; height: 100%; max-width: 100%;">
                      <v-combobox
                        ref="searchEl"
                        :key="refresh"
                        v-model="searchQuery"
                        :items="searchHistoryFiltered"
                        variant="outlined"
                        :placeholder="t('searchField')"
                        :disabled="shortcutScope?.isLoadingBlocking.value ?? false"
                        autofocus
                        class="searchInput"
                        hide-details
                        @blur="() => { if (!searchQuery) { clickSearch(false) } }"
                        @keyup.enter="navigateSearch"
                        @keydown.esc.prevent="navigateBack"
                      >
                        <template #prepend-inner>
                          <v-chip rounded="0" variant="text">
                            <v-icon :icon="mdiMagnifyClose" @click="navigateBack" />
                          </v-chip>
                        </template>
                      </v-combobox>
                    </div>
                  </div>
                  <div v-if="hasPath && !showSearch && !editMode" style="flex-grow: 1">
                    <template v-for="b of breadcrumbs" :key="b.path.join('.')">
                      <v-chip class="path-entry expanded" rounded="0" variant="text" @click="navigatePath(b)">
                        <UIcon v-if="b.icon" :icon="b.icon" size="16" class="mr-1" />
                        {{ b.name }}
                      </v-chip>
                      <UIcon class="expanded" :icon="mdiChevronRight" style="opacity: 0.2" size="16" />
                    </template>
                    <!-- Breadcrumbs -->
                    <v-chip
                      v-if="state.mode !== 'add'" rounded="0" class="file-name" style="font-weight: bold"
                      variant="text" @click="void 0"
                    >
                      <UIcon v-if="currentBreadcrumb.icon" :icon="currentBreadcrumb.icon" class="mr-2" size="22" />
                      {{ currentBreadcrumb.name }}
                    </v-chip>
                    <v-chip v-else variant="text">
                      <UIcon :icon="mdiPlus" />
                      <div>
                        <span v-if="state.addName" class="ml-2">{{ state.addName }}</span>
                      </div>
                    </v-chip>
                  </div>
                  <div v-else-if="editMode" style="flex-grow: 1">
                    <PathAutocomplete
                      v-model:model-value="currentPath"
                      hide-details
                      :show-files="true"
                      select-type="file"
                      no-add
                      style="display: flex; height: 100%"
                    />
                  </div>
                </div>
                <div v-if="!showSearch">
                  <v-chip v-if="editMode" variant="text" rounded="0" @click="() => editMode = !editMode">
                    <v-icon :icon="mdiClose" />
                  </v-chip>
                  <v-chip variant="text" rounded="0" @click="handleEdit()">
                    <v-icon :icon="editMode ? mdiCheck : mdiPencil" />
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </Fitter>
      </div>

      <slot name="append" />
    </div>
  </v-toolbar>
</template>

<style scoped>
.delete-dialog {
    width: 95%;
    max-width: 600px;
}

.file-toolbar {
    padding: 8px;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    flex: 1;
    flex-wrap: wrap;
    justify-content: center;
}

.file-actions {
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    max-height: 32px;
}

.file-path {
    flex-wrap: wrap;
    background: rgb(var(--v-theme-primary3)) !important;
    overflow: hidden;
    flex: 1;
    border-radius: 5px;
    margin-right: 8px;
    display: flex;
    align-items: center;

    >div {
        display: inline-block;
    }

    .path-entry {
        font-size: 0.7em;
    }
}

.compressed {
    display: none !important;
}

.is-breaking {

    .expanded {
        display: none;
    }

    .compressed {
        display: block !important;
    }
}

.file-info {
    container: path / inline-size;
    /* display: contents; */
    display: flex;
    flex: 1;
}

.search-button {
  flex: 1;
  display: flex;
  justify-content: end;
}

:deep(.v-field__input){
  padding-top: 0;
  padding-bottom: 0;
  min-height:0px;
   height:100%;
}

:deep(.v-input__control){
  flex-grow: 1;
}

.search-field {
  display: flex;
  flex-direction: row;
  flex-grow:1;
  height: 100%;
}

.searchInput :deep(.v-field), :deep(.v-field__input){
  padding-left: 0
}
.searchInput{
  display: flex;
}
</style>

<i18n lang="yaml">
  de:
    searchField: 'Suche durch das Projekt...'
  en:
    searchField: 'Search in project...'
</i18n>
